import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle,
  CircleSlash,
  Gauge,
  Info,
  LoaderCircle,
  RefreshCw,
  ShieldAlert,
  Undo2,
  UserCog,
} from 'lucide-react';
import {
  MAX_USER_DAILY_SEND_CAP,
  clearUserSendLimit,
  parseTypedSendLimit,
  requestAdminSendLimits,
  setUserSendLimit,
} from '../utils/adminSendLimitsClient';
import '../styles/AdminSendLimits.css';

// EVERY USER'S DAILY SENDING LIMIT, and the one place an operator changes one.
//
// The number shown is the number the send path will actually use. It is
// resolved by EmailAutomation's `_resolve_daily_send_cap_for_user` in this
// order: the user's own allowance (`sendPolicy/<uid>.dailySendCap`), then the
// runtime's SITESIFT_DAILY_SEND_CAP, then a built-in default of 20.
//
// WHY "WHERE IT COMES FROM" IS A COLUMN AND NOT A FOOTNOTE. Two users can both
// read 20 and behave completely differently: one has been given 20 and will
// keep it when the platform number moves, the other is inheriting and will
// follow. A third can read 20 while a value is stored that the send path
// refuses -- an allowance that is set, visible, and not in force. That last one
// is the fault this rail keeps producing, so it gets its own state rather than
// being rendered as a plain number.

const CAP_REFUSAL =
  `A daily send limit must be a whole number from 1 to ${MAX_USER_DAILY_SEND_CAP}. `
  + 'The send path refuses anything else, and the user would silently keep the '
  + 'platform default.';

function capLabel(value) {
  return value === null ? 'No limit' : String(value);
}

function quoted(value) {
  if (typeof value === 'string') return `"${value}"`;
  if (value === null || value === undefined) return 'nothing';
  return String(value);
}

function decidedByLabel(decidedBy) {
  if (decidedBy === 'operator') return 'An operator';
  if (decidedBy === 'subscription') return 'A subscription';
  if (decidedBy === 'unknown') return 'Unrecorded';
  return '—';
}

function platformDefaultCopy(platformDefault) {
  switch (platformDefault.source) {
    case 'environment':
      return {
        detail: `configured on this runtime as ${platformDefault.configuredValue}`,
        warning: null,
      };
    case 'builtin':
      return {
        detail: 'the built-in fallback',
        warning:
          'SITESIFT_DAILY_SEND_CAP is not set on this runtime, so the number above is '
          + 'the built-in fallback rather than a number anyone chose. The sending '
          + 'runtimes read their own copy of that variable, which may differ from '
          + 'what is shown here.',
      };
    case 'malformed':
      return {
        detail: 'the built-in fallback',
        warning:
          `SITESIFT_DAILY_SEND_CAP is set to ${quoted(platformDefault.configuredValue)}, `
          + 'which is not a number. The built-in fallback stands. Fix the deployed value.',
      };
    case 'disabled':
      return {
        detail: `switched off by SITESIFT_DAILY_SEND_CAP=${quoted(platformDefault.configuredValue)}`,
        warning:
          'The per-user daily rail is switched off on this runtime. Users without an '
          + 'allowance of their own have no daily ceiling at all.',
      };
    default:
      return { detail: 'of unknown origin', warning: 'The platform default could not be read.' };
  }
}

function LimitOrigin({ user }) {
  if (user.limitSource === 'user') {
    return (
      <span className="send-limits-origin is-own" data-testid="limit-origin">
        <UserCog size={14} aria-hidden="true" />
        Set for this user
      </span>
    );
  }
  if (user.limitSource === 'platform') {
    return (
      <span className="send-limits-origin is-inherited" data-testid="limit-origin">
        <Undo2 size={14} aria-hidden="true" />
        Platform default (inherited)
        <small>Follows the platform number when it changes.</small>
      </span>
    );
  }
  if (user.limitSource === 'failClosed') {
    return (
      <span className="send-limits-origin is-danger" data-testid="limit-origin">
        <ShieldAlert size={14} aria-hidden="true" />
        Failing closed
        <small>
          {quoted(user.storedDailySendCap)} is stored, and the send path refuses it. The
          user is held at the conservative ceiling until it is fixed or cleared.
        </small>
      </span>
    );
  }
  return (
    <span className="send-limits-origin" data-testid="limit-origin">
      Unknown
    </span>
  );
}

function SendLimitRow({ user, platformDefault, busy, error, onSet, onClear }) {
  const [draft, setDraft] = useState('');
  const inputId = `send-limit-input-${user.uid}`;
  const clearHintId = `send-limit-clear-hint-${user.uid}`;
  const isBusy = busy === user.uid;
  // Nothing to take away from somebody who never had a number of their own.
  const canClear = user.limitSource === 'user' || user.limitSource === 'failClosed';

  return (
    <tr data-testid={`send-limit-row-${user.uid}`}>
      <th scope="row">
        <span className="send-limits-user">
          <strong>{user.displayName || user.email || user.uid}</strong>
          {user.displayName && user.email && <span>{user.email}</span>}
          <code>{user.uid}</code>
        </span>
      </th>
      <td>
        <span className="send-limits-cap" data-testid="effective-cap">
          {capLabel(user.effectiveDailySendCap)}
          <small>per day</small>
        </span>
      </td>
      <td>
        <LimitOrigin user={user} />
      </td>
      <td>
        <span className="send-limits-usage" data-testid="used-today">
          <strong>{user.usedToday}</strong>
          <small>
            of {capLabel(user.effectiveDailySendCap)}
            {user.remainingToday !== null && ` · ${user.remainingToday} left`}
          </small>
        </span>
      </td>
      <td>
        <span data-testid="decided-by">{decidedByLabel(user.decidedBy)}</span>
      </td>
      <td>
        <div className="send-limits-actions">
          <label className="send-limits-field" htmlFor={inputId}>
            <span className="send-limits-field__label">New daily limit</span>
            <input
              id={inputId}
              type="number"
              min="1"
              max={MAX_USER_DAILY_SEND_CAP}
              step="1"
              inputMode="numeric"
              value={draft}
              disabled={isBusy}
              placeholder="1–1000"
              onChange={event => setDraft(event.target.value)}
            />
          </label>
          <button
            type="button"
            className="send-limits-button is-primary"
            disabled={isBusy}
            onClick={() => onSet(user, draft)}
          >
            Set limit
          </button>
          {canClear && (
            <>
              {/* A DIFFERENT ACTION, not a set with a special number. There is
                  no number that means "inherit": the resolver falls through to
                  the platform only when the field is absent. */}
              <button
                type="button"
                className="send-limits-button is-quiet"
                disabled={isBusy}
                aria-describedby={clearHintId}
                onClick={() => onClear(user)}
              >
                <CircleSlash size={14} aria-hidden="true" />
                Clear allowance
              </button>
              <span id={clearHintId} className="send-limits-hint">
                Removes this user&apos;s own number so they inherit the platform default
                of {capLabel(platformDefault.dailySendCap)} and follow it when it changes.
              </span>
            </>
          )}
          {isBusy && <LoaderCircle size={15} className="send-limits-spin" aria-label="Saving" />}
          {error && (
            <span className="send-limits-row-error" data-testid="row-error" role="alert">
              {error}
            </span>
          )}
        </div>
      </td>
    </tr>
  );
}

export default function AdminSendLimits() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [busyUid, setBusyUid] = useState(null);
  const [rowErrors, setRowErrors] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const next = await requestAdminSendLimits();
      setReport(next);
      setLoadError(null);
    } catch (error) {
      setLoadError(error?.message || 'Failed to read send limits');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const noteRowError = useCallback((uid, message) => {
    setRowErrors(previous => ({ ...previous, [uid]: message }));
  }, []);

  const handleSet = useCallback(async (user, draft) => {
    // Refused here as well as on the server, so a typo costs a keystroke rather
    // than a round trip. The server's copy is the one that matters.
    const cap = parseTypedSendLimit(draft);
    if (cap === null) {
      noteRowError(user.uid, CAP_REFUSAL);
      return;
    }
    setBusyUid(user.uid);
    noteRowError(user.uid, null);
    try {
      await setUserSendLimit({ uid: user.uid, dailySendCap: cap });
      await load();
    } catch (error) {
      noteRowError(user.uid, error?.message || 'Failed to set the send limit');
    } finally {
      setBusyUid(null);
    }
  }, [load, noteRowError]);

  const handleClear = useCallback(async (user) => {
    setBusyUid(user.uid);
    noteRowError(user.uid, null);
    try {
      await clearUserSendLimit({ uid: user.uid });
      await load();
    } catch (error) {
      noteRowError(user.uid, error?.message || 'Failed to clear the send limit');
    } finally {
      setBusyUid(null);
    }
  }, [load, noteRowError]);

  if (loadError) {
    return (
      <section className="admin-send-limits" aria-label="Daily send limits">
        <div className="send-limits-error" role="alert">
          <AlertTriangle size={18} aria-hidden="true" />
          <span>{loadError}</span>
          <button type="button" className="send-limits-button is-quiet" onClick={load}>
            <RefreshCw size={14} aria-hidden="true" />
            Try again
          </button>
        </div>
      </section>
    );
  }

  if (!report) {
    return (
      <section className="admin-send-limits" aria-label="Daily send limits">
        <div className="send-limits-loading">
          <LoaderCircle size={18} className="send-limits-spin" aria-hidden="true" />
          Reading daily send limits…
        </div>
      </section>
    );
  }

  const { platformDefault, summary, usageCaveat } = report;
  const platformCopy = platformDefaultCopy(platformDefault);

  return (
    <section className="admin-send-limits" aria-label="Daily send limits">
      <div className="send-limits-heading">
        <div>
          <span>Sending allowance</span>
          <h3>Daily send limits</h3>
        </div>
        <button
          type="button"
          className="send-limits-button is-quiet"
          disabled={loading}
          onClick={load}
        >
          <RefreshCw size={14} aria-hidden="true" />
          Refresh
        </button>
      </div>

      <div className="send-limits-banner">
        <span className="send-limits-platform" data-testid="platform-default">
          <Gauge size={16} aria-hidden="true" />
          Platform default <strong>{capLabel(platformDefault.dailySendCap)}</strong> per day —
          {' '}{platformCopy.detail}
        </span>
        <span className="send-limits-summary" data-testid="send-limits-summary">
          {summary.withOwnAllowance} of {summary.userCount} on their own number ·
          {' '}{summary.inheritingPlatformDefault} on the platform default ·
          {' '}{summary.failingClosed} failing closed
        </span>
      </div>

      {platformCopy.warning && (
        <p className="send-limits-warning" data-testid="platform-default-warning" role="status">
          <AlertTriangle size={15} aria-hidden="true" />
          {platformCopy.warning}
        </p>
      )}

      {usageCaveat.message && (
        <p className="send-limits-caveat">
          <Info size={15} aria-hidden="true" />
          {usageCaveat.message}
        </p>
      )}

      <div className="send-limits-table-wrap">
        <table aria-label="Daily send limits">
          <thead>
            <tr>
              <th scope="col">User</th>
              <th scope="col">Daily limit</th>
              <th scope="col">Where it comes from</th>
              <th scope="col">Used today ({report.dayKey} UTC)</th>
              <th scope="col">Decided by</th>
              <th scope="col">Change</th>
            </tr>
          </thead>
          <tbody>
            {report.users.map(user => (
              <SendLimitRow
                key={user.uid}
                user={user}
                platformDefault={platformDefault}
                busy={busyUid}
                error={rowErrors[user.uid]}
                onSet={handleSet}
                onClear={handleClear}
              />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
